import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './Layout.scss';

import { Menu, MenuItem, SubMenu } from 'react-pro-sidebar';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import logo from '../assets/image/admin-nav-logo.png';
import logoSmall from '../assets/image/PharmaNex_small.png';

import home from '../assets/image/sidebar/home.svg';
import inventory from '../assets/image/sidebar/inventory.svg';
import sales from '../assets/image/sidebar/sales.svg';
import purchases from '../assets/image/sidebar/purchased.svg';
import report from '../assets/image/sidebar/report.svg';
import settings from '../assets/image/sidebar/settings.svg';

import { clearAdvanceFilter } from '../store/action/advanceFilter';
import { X } from 'lucide-react';

const SIDEBAR_WIDTH = 96;
const SUBMENU_GAP = 18;

const Sidebar = ({ collapsed, setCollapsed, inputRef }) => {
	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();

	const sidebarRef = useRef(null);
	const menuItemRefs = useRef({});

	const [openKeys, setOpenKeys] = useState([]);
	const [projectTitle, setProjectTitle] = useState('Home');

	/*
	 * Collapsed submenu state
	 *
	 * hoveredMenu:
	 * Menu currently hovered.
	 *
	 * pinnedMenu:
	 * Menu opened by click/tap.
	 *
	 * This allows both:
	 * Desktop -> hover
	 * Tablet/iPad -> click
	 * Mobile -> click
	 */
	const [hoveredMenu, setHoveredMenu] = useState(null);
	const [pinnedMenu, setPinnedMenu] = useState(null);

	const [submenuPosition, setSubmenuPosition] = useState({
		top: 0,
		left: SIDEBAR_WIDTH + SUBMENU_GAP,
	});

	const suburl = localStorage.getItem('suburl');

	/*
	 * ---------------------------------------------------------
	 * MENU CONFIGURATION
	 * ---------------------------------------------------------
	 */

	const menuItems = useMemo(
		() => [
			{
				label: 'Home',
				path: `/user/${suburl}/home`,
				icon: home,
			},

			{
				label: 'Inventory',
				path: `/user/${suburl}/product`,
				icon: inventory,
				subMenuItems: [
					{
						label: 'Product',
						path: `/user/${suburl}/product`,
					},
					{
						label: 'Lists',
						path: `/user/${suburl}/lists`,
					},
					{
						label: 'Groups',
						path: `/user/${suburl}/group`,
					},
					{
						label: 'Adjustments',
						path: `/user/${suburl}/inventory-adjustment`,
					},
				],
			},

			{
				label: 'Sales',
				path: `/user/${suburl}/customers`,
				icon: sales,
				subMenuItems: [
					{
						label: 'Customers',
						path: `/user/${suburl}/customers`,
					},
					{
						label: 'Doctors',
						path: `/user/${suburl}/doctors`,
					},
					{
						label: 'Invoices',
						path: `/user/${suburl}/invoices`,
					},
					{
						label: 'Payments Received',
						path: `/user/${suburl}/payments-received`,
					},
					{
						label: 'Sales Return',
						path: `/user/${suburl}/sales-return`,
					},
					{
						label: 'Credit Note',
						path: `/user/${suburl}/sales/credit-note`,
					},
				],
			},

			{
				label: 'Purchases',
				path: `/user/${suburl}/purchases1`,
				icon: purchases,
				subMenuItems: [
					{
						label: 'Vendors',
						path: `/user/${suburl}/vendor`,
					},
					{
						label: 'Purchase Orders',
						path: `/user/${suburl}/purchases-order`,
					},
					{
						label: 'Purchase Receive',
						path: `/user/${suburl}/purchases-receive`,
					},
					{
						label: 'Purchase Bills',
						path: `/user/${suburl}/purchases-bills`,
					},
					{
						label: 'Payment Made',
						path: `/user/${suburl}/payment-made`,
					},
					{
						label: 'Credit Note',
						path: `/user/${suburl}/credit-note`,
					},
				],
			},

			{
				label: 'Reports',
				path: `/user/${suburl}/reports`,
				icon: report,
			},

			{
				label: 'Settings',
				path: `/user/${suburl}/settings`,
				icon: settings,
			},
		],
		[suburl],
	);

	/*
	 * ---------------------------------------------------------
	 * ACTIVE ROUTE
	 * ---------------------------------------------------------
	 */

	const isPathActive = useCallback(
		(path) => {
			if (!path) {
				return false;
			}

			return (
				location.pathname === path ||
				location.pathname.startsWith(`${path}/`)
			);
		},
		[location.pathname],
	);

	const isMenuActive = useCallback(
		(menuItem) => {
			if (isPathActive(menuItem.path)) {
				return true;
			}

			return (
				menuItem.subMenuItems?.some((subMenuItem) =>
					isPathActive(subMenuItem.path),
				) ?? false
			);
		},
		[isPathActive],
	);

	/*
	 * ---------------------------------------------------------
	 * NAVIGATION
	 * ---------------------------------------------------------
	 */

	const closeCollapsedSubmenu = useCallback(() => {
		setHoveredMenu(null);
		setPinnedMenu(null);
	}, []);

	const navigateRoute = useCallback(
		(path, title) => {
			if (!path) {
				return;
			}

			dispatch(clearAdvanceFilter());

			navigate(path);

			if (title) {
				setProjectTitle(title);
			}

			inputRef?.current?.focus();

			closeCollapsedSubmenu();
		},
		[
			dispatch,
			navigate,
			inputRef,
			closeCollapsedSubmenu,
		],
	);

	const onChangeRoute = useCallback(
		(route) => {
			if (!route?.path) {
				return;
			}

			navigateRoute(route.path, route.label);
		},
		[navigateRoute],
	);

	/*
	 * ---------------------------------------------------------
	 * SIDEBAR TOGGLE
	 * ---------------------------------------------------------
	 */

	const toggleClass = () => {
		const root = document.getElementById('root');

		root?.classList.toggle('collapsed');

		setCollapsed((previous) => !previous);

		closeCollapsedSubmenu();
	};

	/*
	 * ---------------------------------------------------------
	 * EXPANDED SUBMENU
	 * ---------------------------------------------------------
	 */

	const handleSubMenuClick = (key) => {
		setOpenKeys((previousKeys) => {
			if (previousKeys.includes(key)) {
				return previousKeys.filter((item) => item !== key);
			}

			return [...previousKeys, key];
		});
	};

	/*
	 * ---------------------------------------------------------
	 * COLLAPSED SUBMENU POSITION
	 * ---------------------------------------------------------
	 *
	 * IMPORTANT:
	 * The panel is positioned based on the actual menu item's
	 * screen position.
	 *
	 * Therefore:
	 *
	 * Inventory -> panel beside Inventory
	 * Sales     -> panel beside Sales
	 * Purchases -> panel beside Purchases
	 *
	 * instead of always opening from the top.
	 */

	const updateSubmenuPosition = useCallback((menuLabel) => {
		const element = menuItemRefs.current[menuLabel];

		if (!element) {
			return;
		}

		const rect = element.getBoundingClientRect();

		const viewportHeight = window.innerHeight;

		const panelEstimatedHeight = 360;

		let top = rect.top;

		/*
		 * Prevent panel from going below viewport.
		 */
		if (top + panelEstimatedHeight > viewportHeight - 16) {
			top = viewportHeight - panelEstimatedHeight - 16;
		}

		/*
		 * Never allow panel to go above viewport.
		 */
		top = Math.max(16, top);

		setSubmenuPosition({
			top,
			left: rect.right + SUBMENU_GAP,
		});
	}, []);

	/*
	 * ---------------------------------------------------------
	 * COLLAPSED MENU HOVER
	 * ---------------------------------------------------------
	 */

	const handleCollapsedMouseEnter = (menuItem) => {
		if (!collapsed) {
			return;
		}

		if (!menuItem.subMenuItems?.length) {
			closeCollapsedSubmenu();
			return;
		}

		setHoveredMenu(menuItem.label);

		updateSubmenuPosition(menuItem.label);
	};

	/*
	 * ---------------------------------------------------------
	 * COLLAPSED MENU CLICK
	 * ---------------------------------------------------------
	 */

	const handleCollapsedMenuClick = (menuItem) => {
		if (!collapsed) {
			return;
		}

		/*
		 * Normal menu item.
		 */
		if (!menuItem.subMenuItems?.length) {
			navigateRoute(menuItem.path, menuItem.label);
			return;
		}

		/*
		 * Parent menu.
		 *
		 * First click:
		 * open/pin
		 *
		 * Second click:
		 * close
		 */
		setPinnedMenu((previous) => {
			if (previous === menuItem.label) {
				setHoveredMenu(null);
				return null;
			}

			setHoveredMenu(menuItem.label);
			updateSubmenuPosition(menuItem.label);

			return menuItem.label;
		});
	};

	/*
	 * ---------------------------------------------------------
	 * PANEL HOVER
	 * ---------------------------------------------------------
	 */

	const handleSubmenuMouseEnter = () => {
		/*
		 * Do nothing.
		 *
		 * Keeping the panel mounted while the mouse enters
		 * prevents the classic sidebar -> submenu gap bug.
		 */
	};

	const handleSubmenuMouseLeave = () => {
		if (!pinnedMenu) {
			setHoveredMenu(null);
		}
	};

	/*
	 * ---------------------------------------------------------
	 * CLOSE PANEL WHEN MOVING TO ANOTHER NON-SUBMENU ITEM
	 * ---------------------------------------------------------
	 */

	const handleCollapsedItemMouseEnter = (menuItem) => {
		if (!menuItem.subMenuItems?.length) {
			closeCollapsedSubmenu();
			return;
		}

		handleCollapsedMouseEnter(menuItem);
	};

	/*
	 * ---------------------------------------------------------
	 * ACTIVE COLLAPSED MENU
	 * ---------------------------------------------------------
	 */

	const activeCollapsedMenu = useMemo(() => {
		if (!hoveredMenu) {
			return null;
		}

		return (
			menuItems.find((item) => item.label === hoveredMenu) || null
		);
	}, [hoveredMenu, menuItems]);

	/*
	 * ---------------------------------------------------------
	 * SYNC OPEN SUBMENU WITH CURRENT ROUTE
	 * ---------------------------------------------------------
	 */

	useEffect(() => {
		const activeMenu = menuItems.find((menuItem) =>
			isMenuActive(menuItem),
		);

		if (activeMenu?.subMenuItems?.length) {
			setOpenKeys([activeMenu.label]);
		}
	}, [location.pathname, menuItems, isMenuActive]);

	/*
	 * ---------------------------------------------------------
	 * RECALCULATE PANEL ON RESIZE / SCROLL
	 * ---------------------------------------------------------
	 */

	useEffect(() => {
		if (!collapsed || !hoveredMenu) {
			return undefined;
		}

		const handlePositionUpdate = () => {
			updateSubmenuPosition(hoveredMenu);
		};

		window.addEventListener('resize', handlePositionUpdate);
		window.addEventListener('scroll', handlePositionUpdate, true);

		return () => {
			window.removeEventListener(
				'resize',
				handlePositionUpdate,
			);

			window.removeEventListener(
				'scroll',
				handlePositionUpdate,
				true,
			);
		};
	}, [
		collapsed,
		hoveredMenu,
		updateSubmenuPosition,
	]);

	/*
	 * ---------------------------------------------------------
	 * ESCAPE KEY
	 * ---------------------------------------------------------
	 */

	useEffect(() => {
		const handleEscape = (event) => {
			if (event.key === 'Escape') {
				closeCollapsedSubmenu();
			}
		};

		document.addEventListener('keydown', handleEscape);

		return () => {
			document.removeEventListener('keydown', handleEscape);
		};
	}, [closeCollapsedSubmenu]);

	/*
	 * ---------------------------------------------------------
	 * DOCUMENT TITLE
	 * ---------------------------------------------------------
	 */

	useEffect(() => {
		document.title = `${projectTitle} | Pharmnex`;
	}, [projectTitle]);

	/*
	 * ---------------------------------------------------------
	 * INITIAL SEARCH FOCUS
	 * ---------------------------------------------------------
	 */

	useEffect(() => {
		inputRef?.current?.focus();
	}, [inputRef]);

	/*
	 * ---------------------------------------------------------
	 * RENDER
	 * ---------------------------------------------------------
	 */

	return (
		<div
			ref={sidebarRef}
			className="sidebar-wrapper"
		>
			<Menu className="side-menu">
				{/* =============================================
				    BRAND
				    ============================================= */}

				<div className="brand-name-sidebar">
					{collapsed ? (
						<div
							className="small-logo"
							onClick={toggleClass}
							role="button"
							tabIndex={0}
							onKeyDown={(event) => {
								if (
									event.key === 'Enter' ||
									event.key === ' '
								) {
									toggleClass();
								}
							}}
						>
							<img
								src={logoSmall}
								alt="PharmNex"
								className="sidebar-logo-small"
							/>
						</div>
					) : (
						<div
							className="brand-name"
							onClick={toggleClass}
							role="button"
							tabIndex={0}
							onKeyDown={(event) => {
								if (
									event.key === 'Enter' ||
									event.key === ' '
								) {
									toggleClass();
								}
							}}
						>
							<img
								src={logo}
								alt="PharmNex"
								className="sidebar-logo"
							/>
						</div>
					)}
				</div>

				{/* =============================================
				    EXPANDED MENU
				    ============================================= */}

				{!collapsed && (
					<div className="menu-items">
						{menuItems.map((menuItem) => {
							const active = isMenuActive(menuItem);

							if (menuItem.subMenuItems?.length) {
								return (
									<SubMenu
										key={menuItem.label}
										label={menuItem.label}
										icon={
											<img
												src={menuItem.icon}
												alt=""
												title={menuItem.label}
											/>
										}
										open={openKeys.includes(
											menuItem.label,
										)}
										onOpenChange={() =>
											handleSubMenuClick(
												menuItem.label,
											)
										}
										transitionDuration={300}
										className={
											active
												? 'parent-menu-active'
												: ''
										}
									>
										{menuItem.subMenuItems.map(
											(subMenuItem) => (
												<MenuItem
													key={
														subMenuItem.label
													}
													className={
														isPathActive(
															subMenuItem.path,
														)
															? 'active'
															: ''
													}
													onClick={() =>
														onChangeRoute(
															subMenuItem,
														)
													}
												>
													<Link
														to={
															subMenuItem.path
														}
														onClick={(event) =>
															event.preventDefault()
														}
													>
														<span>
															{
																subMenuItem.label
															}
														</span>
													</Link>
												</MenuItem>
											),
										)}
									</SubMenu>
								);
							}

							return (
								<MenuItem
									key={menuItem.label}
									className={
										active ? 'active' : ''
									}
									icon={
										<img
											src={menuItem.icon}
											alt=""
											title={menuItem.label}
										/>
									}
									onClick={() =>
										navigateRoute(
											menuItem.path,
											menuItem.label,
										)
									}
								>
									<Link
										to={menuItem.path}
										onClick={(event) =>
											event.preventDefault()
										}
									>
										<span>{menuItem.label}</span>
									</Link>
								</MenuItem>
							);
						})}
					</div>
				)}

				{/* =============================================
				    COLLAPSED MENU
				    ============================================= */}

				{collapsed && (
					<div className="collapsed-menu-items">
						{menuItems.map((menuItem) => {
							const active = isMenuActive(menuItem);
							const hasChildren =
								menuItem.subMenuItems?.length > 0;

							return (
								<div
									key={menuItem.label}
									ref={(element) => {
										menuItemRefs.current[
											menuItem.label
										] = element;
									}}
									className={[
										'collapsed-menu-item',
										active ? 'is-active' : '',
										hoveredMenu === menuItem.label
											? 'is-open'
											: '',
									]
										.filter(Boolean)
										.join(' ')}
									onMouseEnter={() =>
										handleCollapsedItemMouseEnter(
											menuItem,
										)
									}
									onClick={() =>
										handleCollapsedMenuClick(
											menuItem,
										)
									}
								>
									<div className="collapsed-menu-button">
										<img
											src={menuItem.icon}
											alt=""
											className="collapsed-menu-icon"
										/>

										{!hasChildren && (
											<span className="collapsed-menu-tooltip">
												<img
													src={menuItem.icon}
													alt=""
													className="collapsed-menu-tooltip-icon"
												/>

												<span>{menuItem.label}</span>
											</span>
										)}
									</div>

									{hasChildren && (
										<span className="collapsed-menu-arrow">›</span>
									)}
								</div>
							);
						})}
					</div>
				)}
			</Menu>

			{/* =============================================
			    FLOATING SUBMENU
			    ============================================= */}

			{collapsed &&
				activeCollapsedMenu?.subMenuItems?.length > 0 && (
					<div
						className="collapsed-submenu-panel"
						style={{
							top: `${submenuPosition.top}px`,
							left: `${submenuPosition.left}px`,
						}}
						onMouseEnter={handleSubmenuMouseEnter}
						onMouseLeave={handleSubmenuMouseLeave}
					>
						<div className="collapsed-submenu-header">
							<div className="collapsed-submenu-heading">
								<img
									src={activeCollapsedMenu.icon}
									alt=""
								/>

								<span>
									{activeCollapsedMenu.label}
								</span>
							</div>

							<button
								type="button"
								className="collapsed-submenu-close"
								onClick={(event) => {
									event.stopPropagation();
									closeCollapsedSubmenu();
								}}
								aria-label="Close submenu"
							>
								<X size={18} strokeWidth={2} aria-hidden="true" />
							</button>
						</div>

						<div className="collapsed-submenu-list">
							{activeCollapsedMenu.subMenuItems.map(
								(subMenuItem) => {
									const active = isPathActive(
										subMenuItem.path,
									);

									return (
										<Link
											key={subMenuItem.label}
											to={subMenuItem.path}
											className={[
												'collapsed-submenu-link',
												active
													? 'active'
													: '',
											]
												.filter(Boolean)
												.join(' ')}
											onClick={(event) => {
												event.preventDefault();
												event.stopPropagation();

												onChangeRoute(
													subMenuItem,
												);
											}}
										>
											<span className="collapsed-submenu-indicator" />

											<span className="collapsed-submenu-label">
												{
													subMenuItem.label
												}
											</span>
										</Link>
									);
								},
							)}
						</div>
					</div>
				)}
		</div>
	);
};

export default Sidebar;